import * as moment from 'moment';
import { FORMATO_FECHA, FORMATO_MOMENTO } from './compartido.static';
import { formatCurrency } from '@angular/common';
import { ErrorDesarrollo } from './error.utils';

/**
 * Devuelve si el string está vacío.
 * @param string - String a comprobar.
 * @returns {boolean} - Si el string está vacío.
 * @author Juan Corral
 */
export function stringVacío(string: string | null | undefined): boolean {
  return string === null || string === undefined || string === '';
}

/**
 * Devuelve si el número está vacío.
 * @param numero - Número a comprobar.
 * @returns {boolean} - Si el número está vacío.
 * @author Juan Corral
 */
export function numeroVacío(numero: number | null | undefined): boolean {
  return numero === null || numero === undefined || isNaN(numero);
}

/**
 * Devuelve si el valor está vacío.
 * @param valor - El valor a comprobar.
 * @returns {boolean} - Si el valor está vacío.
 * @author Juan Corral
 */
export function valorVacío(valor: number | string | null | undefined): boolean {
  if (typeof valor === 'number') return numeroVacío(valor);
  return stringVacío(valor);
}

/**
 * Devuelve el valor como número o undefined si no se pudo formatear.
 * @param {string | number | undefined} valor - El valor a formatear.
 * @param {'int' | 'float'} tipo - El tipo de número a formatear.
 * @returns {number | undefined} - El string como número o undefined si no se pudo formatear.
 * @author Juan Corral
 */
export function valorNumérico(
  valor: string | number | undefined | null,
  tipo: 'int' | 'float' = 'int',
): number | undefined {
  if (valor === undefined) return undefined;

  let nuevoNumero: number | null;
  if (typeof valor === 'string') {
    nuevoNumero = parseInt(valor);
    if (tipo == 'float') nuevoNumero = parseFloat(valor);
  } else {
    nuevoNumero = valor;
  }
  if (nuevoNumero === null || isNaN(nuevoNumero)) return undefined;
  return nuevoNumero;
}

/**
 * Devuelve el string con la primera letra de cada palabra en mayúscula.
 * @param {string | undefined | null} str - El string a capitalizar.
 * @returns {string} - El string capitalizado.
 * @author Juan Corral
 */
export function capitalizar(str: string | undefined | null): string {
  if (str === undefined || str === null) return '';
  str = str.trim();
  return str
    .split(' ')
    .map((palabra) => {
      if (palabra.length === 0) return '';
      return palabra[0].toUpperCase() + palabra.substring(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Formatea el valor par moneda colombiana.
 * @param {string | number} valor - El valor a formatear.
 * @returns {string} - El valor formateado o '' si no se pudo formatear.
 * @author Juan Corral
 */
export function formatearMoneda(valor: string | number): string {
  const numero = valorNumérico(valor);
  if (numero === undefined) return '';
  const formateado = formatCurrency(numero, 'es_CO', '', 'COP', '1.0-0');
  if (valorVacío(formateado)) return '';
  return '$' + formateado;
}

/**
 * Formatea la fecha en el formato especificado.
 * @param {string | number | Date | moment.Moment} valor - La fecha a formatear.
 * @param {string} formato - El formato de fecha a formatear.
 * @returns {string} - La fecha formateada en el formato especificado o '' si no se pudo formatear.
 * @author Juan Corral
 */
export function formatearFecha(
  valor: string | number | Date | moment.Moment,
  formato = FORMATO_FECHA,
): string {
  return formatearMomento(valor, formato);
}

/**
 * Formatea el momento (fecha y hora) en el formato especificado.
 * @param {string | number | Date | moment.Moment} valor - El momento a formatear.
 * @param {string} formato - El formato de momento a formatear.
 * @returns {string} - El momento formateado en el formato especificado o '' si no se pudo formatear.
 * @author Juan Corral
 */
export function formatearMomento(
  valor: string | number | Date | moment.Moment,
  formato = FORMATO_MOMENTO,
): string {
  if (moment.isMoment(valor)) return valor.format(formato);
  return moment(valor).format(formato);
}

/**
 * Convierte un string de fecha a un objeto moment.
 * @param {string} fecha - La fecha a convertir.
 * @returns {moment.Moment | undefined} - La fecha convertida o undefined si no se pudo convertir.
 * @author Juan Corral
 */
export function fechaToMoment(fecha: string): moment.Moment | undefined {
  return moment(fecha);
}

/**
 * Convierte un string de fecha a un objeto Date.
 * * Preferible a Date(fecha) ya que Date carga la fecha en UTC.
 * @param {string} fecha - La fecha a convertir.
 * @returns {moment.Moment | undefined} - La fecha convertida o undefined si no se pudo convertir la fecha.
 * @author Juan Corral
 */
export function fechaToDate(fecha: string): Date | undefined {
  return fechaToMoment(fecha)?.toDate();
}

/**
 * Suma horas a una fecha.
 * @param {Date} fecha - La fecha a la que se le sumarán las horas.
 * @param {number} horas - Las horas a sumar.
 * @returns {Date} - La fecha con las horas sumadas.
 * @author Juan Corral
 */
export function sumarHoras(fecha: Date, horas: number): Date {
  return moment(fecha).add(horas, 'hours').toDate();
}

/**
 * Formatea el texto a UTF-8.
 * @param {string} texto - El texto a formatear.
 * @returns {string} - El texto formateado a UTF-8.
 * @author Juan Corral
 */
export function formatearUTF8(texto: string) {
  const decoder = new TextDecoder('utf-8');
  const encoder = new TextEncoder();

  // Convertir string a bytes
  const bytes = encoder.encode(texto);
  // Convert bytes devuelta a string
  let decodificado = decoder.decode(bytes);

  // TODO: Esto es un parche, hay que arreglarlo
  decodificado = decodificado.replace(/Ã¡/g, 'á');
  decodificado = decodificado.replace(/Ã©/g, 'é');
  decodificado = decodificado.replace(/Ã­/g, 'í');
  decodificado = decodificado.replace(/Ã³/g, 'ó');
  decodificado = decodificado.replace(/Ãº/g, 'ú');
  decodificado = decodificado.replace(/Ã‘/g, 'ñ');

  return decodificado;
}

/**
 * Normaliza un string (Minúsculas, sin acentos, y sin caracteres especiales).
 * @param {string} texto - El texto a normalizar.
 * @param {boolean} quitarEspacios - Si se deben quitar los espacios en blanco.
 * @returns {string} - El texto normalizado.
 * @author Juan Corral
 */
export function normalizarString(texto: string, quitarEspacios: boolean = false): string {
  let resultado = texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (quitarEspacios) resultado = resultado.replace(/\s+/g, ' ').trim();
  return resultado;
}

/**
 * Devuelve un string aleatorio.
 * @param {number} tamaño - El tamaño del string.
 * @returns {string} - El string aleatorio.
 * @author Juan Corral
 */
export function stringAleatorio(tamaño: number): string {
  const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let resultado = '';
  for (let i = 0; i < tamaño; i++) {
    resultado += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
  }
  return resultado;
}

/**
 * Acorta un string a un tamaño máximo, añadiendo "..." al final si se ha acortado.
 * @param {string} texto - El texto a acortar.
 * @param {number} tamaño - El tamaño máximo del texto.
 * @returns {string} - El texto acortado.
 * @author Juan Corral
 */
export function acortarString(texto: string, tamaño: number): string {
  if (tamaño < 3) throw new ErrorDesarrollo('El tamaño debe ser mayor o igual a 3');
  if (texto.length <= tamaño) return texto;
  return texto.substring(0, tamaño - 3) + '...';
}

/**
 * Calcula la distancia entre dos coordenadas en km usando la fórmula de Haversine.
 * @param {Object} coordenada1 - La primera coordenada con lat y lon.
 * @param {Object} coordenada2 - La segunda coordenada con lat y lon.
 * @returns {number} - La distancia entre las dos coordenadas en km.
 * @author Juan Corral
 */
export function distancia(
  coordenada1: { lat: number; lon: number },
  coordenada2: { lat: number; lon: number },
): number {
  function deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }
  const R = 6371; // Radius of the Earth in km
  const dLat = deg2rad(coordenada2.lat - coordenada1.lat);
  const dLon = deg2rad(coordenada2.lon - coordenada1.lon);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(coordenada1.lat)) *
      Math.cos(deg2rad(coordenada2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
}
